import type { Schedule } from "../../types";
import { formatTime } from "../../lib/dateUtils";
import { getLocalizedName } from "../../lib/localization";
import { useEdition } from "../../hooks/useEdition";
import { useTranslations } from "../../hooks/useTranslations";

type ScheduleItemProps = {
  schedule: Schedule;
  itemId?: string;
  onArtistClick?: (artistId: string) => void;
  onEventClick?: (eventId: string) => void;
  showPlace?: boolean;
};

export function ScheduleItem({
  schedule,
  itemId,
  onArtistClick,
  onEventClick,
  showPlace = false,
}: ScheduleItemProps) {
  const { locale, edition, timeFormat } = useEdition();
  const t = useTranslations();
  const fallback = edition?.default_locale_code;
  const hasTime = !!schedule.start_time;
  const eventName = schedule.event
    ? getLocalizedName(schedule.event, locale, fallback)
    : undefined;
  const name = schedule.artist?.name ?? eventName ?? "Unnamed";

  const handleClick = () => {
    if (schedule.artist && onArtistClick) {
      onArtistClick(schedule.artist._id);
    } else if (schedule.event && onEventClick) {
      onEventClick(schedule.event._id);
    }
  };

  return (
    <button
      type="button"
      className="festapp-schedule-item"
      data-item-id={itemId}
      onClick={handleClick}
    >
      {hasTime && (
        <div className="festapp-schedule-item-time">
          <span className="festapp-schedule-item-start-time">{formatTime(schedule.start_time, timeFormat)}</span>
          {schedule.end_time && (
            <span className="festapp-schedule-item-end-time">
              {formatTime(schedule.end_time, timeFormat)}
            </span>
          )}
        </div>
      )}
      <div className="festapp-schedule-item-content">
        <span className="festapp-schedule-item-name" style={{ whiteSpace: 'normal' }}>{name}</span>
        {showPlace && schedule.place && (
          <span className="festapp-schedule-item-place" style={{ whiteSpace: 'normal' }}>
            {getLocalizedName(schedule.place, locale, fallback)}
          </span>
        )}
        {schedule.categories && schedule.categories.length > 0 && (
          <span className="festapp-schedule-item-categories" style={{ whiteSpace: 'normal' }}>
            {schedule.categories
              .map((c) => getLocalizedName(c, locale, fallback))
              .filter(Boolean)
              .join(", ")}
          </span>
        )}
      </div>
      <div className="festapp-schedule-item-action">
        {schedule.ticket_url && (
          <a
            href={schedule.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="festapp-schedule-item-ticket"
            onClick={(e) => e.stopPropagation()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
              <path d="M13 5v2" />
              <path d="M13 17v2" />
              <path d="M13 11v2" />
            </svg>
            <span>{t.getTickets}</span>
          </a>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="festapp-schedule-item-chevron"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </button>
  );
}
